import { useEffect } from "react";

const SITE_BASE = "https://kisscorner.github.io";

interface PageMetaOptions {
  title: string;
  description: string;
  keywords?: string;
  jsonLd?: object[];
}

function upsertMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function buildCanonical(): string {
  const basePath = __BASE_PATH__.split("/").filter(Boolean).join("/");
  const pathPrefix = basePath ? `/${basePath}` : "";
  const stripped = window.location.pathname.replace(pathPrefix, "").replace(/\/$/, "");
  return `${SITE_BASE}${pathPrefix}${stripped || "/"}`;
}

export function usePageMeta({ title, description, keywords, jsonLd }: PageMetaOptions) {
  useEffect(() => {
    document.title = title;
    upsertMeta("name", "description", description);
    upsertMeta("name", "keywords", keywords || "");
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);

    const canonical = buildCanonical();
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", canonical);
    upsertMeta("property", "og:url", canonical);

    const scripts: HTMLScriptElement[] = [];
    jsonLd?.forEach((schema, index) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-page-jsonld", `page-${index}`);
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      scripts.push(script);
    });

    return () => {
      scripts.forEach((s) => s.remove());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, keywords]);
}